// OrganisationSeeding — Admin tab for bulk uploading organisations via JSON/CSV

import {
  AlertCircle,
  CheckCircle,
  Download,
  FileJson,
  FileSpreadsheet,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import { Alert, AlertDescription } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { useToast } from "../../hooks/use-toast";
import { supabase } from "../../utils/supabaseClient";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

type OrgRow = {
  name: string;
  description?: string;
  tenant_type?: "personal" | "organization";
};

type ParseResult = {
  rows: OrgRow[];
  errors: string[];
};

type SeedResult = {
  created: number;
  skipped: number;
  errors: string[];
};

const SAMPLE_JSON: OrgRow[] = [
  {
    name: "Bondi Surf Life Saving Club",
    description: "Surf lifesaving club based in Bondi Beach",
    tenant_type: "organization",
  },
  {
    name: "Melbourne Tigers Basketball",
    description: "Community basketball club in Melbourne",
    tenant_type: "organization",
  },
  {
    name: "Byron Bay Rugby Union",
    description: "Rugby union club in Byron Bay region",
    tenant_type: "organization",
  },
];

const SAMPLE_CSV = `name,description,tenant_type
Bondi Surf Life Saving Club,Surf lifesaving club based in Bondi Beach,organization
Melbourne Tigers Basketball,Community basketball club in Melbourne,organization
Byron Bay Rugby Union,Rugby union club in Byron Bay region,organization`;

const getAuthHeaders = async (): Promise<HeadersInit> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("No active session");
  }

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${session.access_token}`,
  };
};

// Parse CSV string into OrgRow[]
const parseCsv = (text: string): ParseResult => {
  const lines = text.trim().split("\n");
  const errors: string[] = [];
  const rows: OrgRow[] = [];

  if (lines.length < 2) {
    return { rows: [], errors: ["CSV file must have a header row and at least one data row."] };
  }

  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const nameIdx = headers.indexOf("name");

  if (nameIdx === -1) {
    return { rows: [], errors: ['CSV must have a "name" column.'] };
  }

  const descIdx = headers.indexOf("description");
  const typeIdx = headers.indexOf("tenant_type");

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const cols = line.split(",").map((c) => c.trim());
    const name = cols[nameIdx];

    if (!name) {
      errors.push(`Row ${i + 1}: missing name, skipped.`);
      continue;
    }

    const tenantType = typeIdx !== -1 ? cols[typeIdx] : undefined;
    if (tenantType && tenantType !== "personal" && tenantType !== "organization") {
      errors.push(`Row ${i + 1}: invalid tenant_type "${tenantType}", defaulting to "organization".`);
    }

    rows.push({
      name,
      description: descIdx !== -1 ? cols[descIdx] || undefined : undefined,
      tenant_type:
        tenantType === "personal" || tenantType === "organization"
          ? tenantType
          : "organization",
    });
  }

  return { rows, errors };
};

// Parse JSON string into OrgRow[]
const parseJson = (text: string): ParseResult => {
  const errors: string[] = [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { rows: [], errors: ["Invalid JSON format."] };
  }

  const arr = Array.isArray(parsed) ? parsed : [parsed];
  const rows: OrgRow[] = [];

  for (let i = 0; i < arr.length; i++) {
    const item = arr[i];
    if (!item || typeof item !== "object") {
      errors.push(`Item ${i + 1}: not a valid object, skipped.`);
      continue;
    }

    const name = (item as Record<string, unknown>).name;
    if (!name || typeof name !== "string") {
      errors.push(`Item ${i + 1}: missing or invalid "name", skipped.`);
      continue;
    }

    const desc = (item as Record<string, unknown>).description;
    const tenantType = (item as Record<string, unknown>).tenant_type;

    if (tenantType && tenantType !== "personal" && tenantType !== "organization") {
      errors.push(`Item ${i + 1}: invalid tenant_type "${String(tenantType)}", defaulting to "organization".`);
    }

    rows.push({
      name: name.trim(),
      description: typeof desc === "string" ? desc : undefined,
      tenant_type:
        tenantType === "personal" || tenantType === "organization"
          ? tenantType
          : "organization",
    });
  }

  return { rows, errors };
};

export const OrganisationSeeding: React.FC = () => {
  const [parsedRows, setParsedRows] = useState<OrgRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [seedResult, setSeedResult] = useState<SeedResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      setSeedResult(null);
      const file = e.target.files?.[0];
      if (!file) return;

      setFileName(file.name);
      const text = await file.text();

      let result: ParseResult;
      if (file.name.endsWith(".json")) {
        result = parseJson(text);
      } else if (file.name.endsWith(".csv")) {
        result = parseCsv(text);
      } else {
        setParseErrors(["Unsupported file type. Please upload a .json or .csv file."]);
        setParsedRows([]);
        return;
      }

      setParsedRows(result.rows);
      setParseErrors(result.errors);

      if (result.rows.length === 0 && result.errors.length === 0) {
        setParseErrors(["No valid organisation rows found in the file."]);
      }
    },
    []
  );

  const handleClear = () => {
    setParsedRows([]);
    setParseErrors([]);
    setFileName(null);
    setSeedResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (parsedRows.length === 0) return;

    setIsUploading(true);
    setSeedResult(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/admin/org-seeding`, {
        method: "POST",
        headers,
        body: JSON.stringify({ organisations: parsedRows }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `Server responded with ${response.status}`);
      }

      const data = await response.json();
      setSeedResult(data);

      toast({
        title: "Seeding Complete",
        description: `Created ${data.created} organisation(s). ${data.skipped} skipped.`,
      });
    } catch (error) {
      console.error("Organisation seeding failed:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to seed organisations.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const downloadSample = (format: "json" | "csv") => {
    const content = format === "json" ? JSON.stringify(SAMPLE_JSON, null, 2) : SAMPLE_CSV;
    const mimeType = format === "json" ? "application/json" : "text/csv";
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sample-organisations.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Organisation Seeding</h1>
        <p className="text-muted-foreground mt-1">
          Bulk upload organisations by uploading a JSON or CSV file.
        </p>
      </div>

      {/* Sample Downloads */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sample Templates</CardTitle>
          <CardDescription>
            Download a sample file to see the expected format, then fill in your
            organisation data.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => downloadSample("json")}>
              <FileJson className="mr-2 h-4 w-4" />
              Download Sample JSON
            </Button>
            <Button variant="outline" onClick={() => downloadSample("csv")}>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Download Sample CSV
            </Button>
          </div>
          <div className="mt-4 text-sm text-muted-foreground space-y-1">
            <p>
              <strong>Required fields:</strong> <code>name</code>
            </p>
            <p>
              <strong>Optional fields:</strong> <code>description</code>,{" "}
              <code>tenant_type</code> (defaults to &quot;organization&quot;)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* File Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Upload File</CardTitle>
          <CardDescription>
            Select a .json or .csv file containing organisation data.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-2 h-4 w-4" />
              Choose File
            </Button>
            {fileName && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{fileName}</Badge>
                <button
                  onClick={handleClear}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Parse Errors */}
      {parseErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc pl-4 space-y-1">
              {parseErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Seed Result */}
      {seedResult && (
        <Alert variant={seedResult.errors.length > 0 ? "destructive" : "default"}>
          {seedResult.errors.length > 0 ? (
            <AlertCircle className="h-4 w-4" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
          <AlertDescription>
            <p className="font-medium">
              Created: {seedResult.created} | Skipped: {seedResult.skipped}
            </p>
            {seedResult.errors.length > 0 && (
              <ul className="list-disc pl-4 mt-2 space-y-1">
                {seedResult.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Preview Table */}
      {parsedRows.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  Preview ({parsedRows.length} organisation
                  {parsedRows.length !== 1 ? "s" : ""})
                </CardTitle>
                <CardDescription>
                  Review the data below before uploading.
                </CardDescription>
              </div>
              <Button onClick={handleUpload} disabled={isUploading}>
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Seed {parsedRows.length} Organisation
                    {parsedRows.length !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parsedRows.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-muted-foreground">
                      {idx + 1}
                    </TableCell>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.description || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {row.tenant_type || "organization"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
